/**
 * Dynamic documentation URL helper.
 * Generates docs URLs that work on local, staging, and production
 * without any hardcoded domain.
 *
 * Local:   http://localhost:3000/docs/
 * Staging: https://stage.genomequestlive.com/fulltext/docs/
 * Prod:    https://genomequestlive.com/fulltext/docs/
 */

/**
 * Returns the base docs directory URL for the current environment.
 * @returns {string} e.g. "http://localhost:3000/docs/"
 */
export const getDocsBaseUrl = () => {
    const pathBeforeHash = window.location.href.split('#')[0];
    const basePath = pathBeforeHash.substring(0, pathBeforeHash.lastIndexOf('/') + 1);
    return `${basePath}docs/`;
};

const sanitizeDocSection = (section) => {
    if (!section || typeof section !== 'string') {
        return 'main_content';
    }
    const value = section.trim();
    return /^[A-Za-z0-9_]+$/.test(value) ? value : 'main_content';
};

const sanitizeDocAnchor = (anchor) => {
    if (!anchor || typeof anchor !== 'string') {
        return '';
    }
    const value = anchor.trim();
    return /^[A-Za-z0-9_:.\-]+$/.test(value) ? value : '';
};

/**
 * Generates a full documentation URL for a specific page and optional anchor.
 * Routes through docs/index.html which provides the sidebar, search, and styling.
 *
 * @param {string} page   - HTML file name relative to docs/ (default: 'index.html')
 * @param {string} anchor - Optional hash anchor (without #)
 * @returns {string} Full docs URL
 *
 * Examples:
 *   getDocsUrl()                                                → {base}docs/index.html
 *   getDocsUrl('index.html', 'FT_QuerySyntax')                 → {base}docs/index.html#FT_QuerySyntax
 *   getDocsUrl('ip_sequence_searching.html')                    → {base}docs/index.html?s=ip_sequence_searching
 *   getDocsUrl('ip_sequence_searching.html', 'querysequenceinput') → {base}docs/index.html?s=ip_sequence_searching&a=querysequenceinput
 *   getDocsUrl('sequence_comparison_algorithms.html', 'blast')  → {base}docs/index.html?s=sequence_comparison_algorithms&a=blast
 */
export const getDocsUrl = (page = 'index.html', anchor = '') => {
    const base = getDocsBaseUrl();
    const safeAnchor = sanitizeDocAnchor(anchor);
    // If it's index.html itself (or no page), just link to index directly
    if (!page || page === 'index.html') {
        const hash = safeAnchor ? `#${encodeURIComponent(safeAnchor)}` : '';
        return `${base}index.html${hash}`;
    }
    // Strip .html extension to get the section name for the ?s= parameter
    const section = sanitizeDocSection(page.replace(/\.html$/, ''));
    const anchorParam = safeAnchor ? `&a=${encodeURIComponent(safeAnchor)}` : '';
    return `${base}index.html?s=${encodeURIComponent(section)}${anchorParam}`;
};
